import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getCategoryName, getComicById, getRelatedComics } from '@/utils/comicHelpers';
import { useSeo } from '@/hooks/useSeo';

export default function ComicDetail() {
  const { id } = useParams();
  const comic = getComicById(id);
  const [favorited, setFavorited] = useState(false);
  const [reverse, setReverse] = useState(false);
  const [toast, setToast] = useState('');

  const related = useMemo(() => (comic ? getRelatedComics(comic, 4) : []), [comic]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 2000);
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast('链接已复制，分享给朋友吧');
    } catch {
      showToast('复制失败，请手动复制地址栏链接');
    }
  };

  useSeo({
    title: comic
      ? `《${comic.title}》漫画-在线免费阅读全章节-布丁漫画网`
      : '漫画不存在-布丁漫画网',
    description: comic
      ? `《${comic.title}》漫画,在线免费阅读,布丁漫画网。${comic.description} 全集中文版本在线免费阅读，每日更新，无需付费。`
      : '您访问的漫画不存在，请返回布丁漫画网继续阅读其他作品。',
    keywords: comic
      ? `${comic.title},漫画,在线免费阅读,布丁漫画网,${getCategoryName(comic.categories[0])}漫画`
      : '布丁漫画网,漫画,在线免费阅读',
    path: `/comic/${id ?? ''}`,
    ogImage: comic?.cover,
    jsonLd: comic
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: `${window.location.origin}${__BASE_PATH__ || ''}/`,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: `${getCategoryName(comic.categories[0])}漫画`,
                item: `${window.location.origin}${__BASE_PATH__ || ''}/category/${comic.categories[0]}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: comic.title,
                item: `${window.location.origin}${__BASE_PATH__ || ''}/comic/${comic.id}`,
              },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'ComicStory',
            name: `《${comic.title}》漫画`,
            author: { '@type': 'Person', name: comic.author },
            description: comic.description,
            image: comic.cover,
            dateModified: comic.updateTime,
            inLanguage: 'zh-CN',
            numberOfEpisodes: comic.chapters.length,
            url: `${window.location.origin}${__BASE_PATH__ || ''}/comic/${comic.id}`,
          },
        ]
      : [],
  });

  if (!comic) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-24 text-center md:px-6">
        <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
        <h1 className="mt-4 text-2xl text-foreground-950">漫画走丢了</h1>
        <p className="mt-2 text-sm text-foreground-600">
          您访问的漫画不存在或已下架，去看看其他热门作品吧。
        </p>
        <Link
          to="/category"
          className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
        >
          浏览全部漫画
        </Link>
      </div>
    );
  }

  const chapters = reverse ? [...comic.chapters].reverse() : comic.chapters;
  const categoryName = getCategoryName(comic.categories[0]);
  const totalStars = 5;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
      {toast && (
        <div className="fixed left-1/2 top-20 z-[60] -translate-x-1/2 whitespace-nowrap rounded-full bg-foreground-950/90 px-5 py-2.5 text-sm text-background-50 shadow-lg">
          {toast}
        </div>
      )}

      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: `${categoryName}漫画`, to: `/category/${comic.categories[0]}` },
          { label: comic.title },
        ]}
      />

      <section className="mt-5 grid grid-cols-1 gap-6 rounded-2xl bg-background-100 p-5 md:p-6 lg:grid-cols-[260px_1fr]">
        <div className="mx-auto w-48 lg:mx-0 lg:w-full">
          <div className="aspect-[3/4] w-full overflow-hidden rounded-xl bg-background-200">
            <img
              src={comic.cover}
              alt={`《${comic.title}》漫画封面`}
              title={`《${comic.title}》漫画在线免费阅读`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>

        <div className="flex flex-col">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl text-foreground-950 md:text-3xl">
              《{comic.title}》
            </h1>
            <span
              className={`rounded-md px-2 py-0.5 text-xs ${
                comic.status === '连载中'
                  ? 'bg-accent-100 text-accent-700'
                  : 'bg-secondary-100 text-secondary-900'
              }`}
            >
              {comic.status}
            </span>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {comic.categories.map((cat) => (
              <Link
                key={cat}
                to={`/category/${cat}`}
                className="whitespace-nowrap rounded-full bg-background-50 px-3 py-1 text-xs text-primary-600 transition-colors hover:bg-primary-500 hover:text-background-50"
              >
                {getCategoryName(cat)}
              </Link>
            ))}
            <span className="flex items-center gap-1 text-sm font-semibold text-accent-600">
              <i className="ri-star-fill"></i>
              {comic.rating}
              <span className="font-normal text-foreground-500">分</span>
            </span>
            {Array.from({ length: totalStars }).map((_, i) => (
              <i
                key={`star-${i}`}
                className={`text-sm ${
                  i < Math.round(comic.rating / 2)
                    ? 'ri-star-fill text-accent-500'
                    : 'ri-star-line text-foreground-300'
                }`}
              ></i>
            ))}
          </div>

          <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            {[
              { label: '作者', value: comic.author },
              { label: '更新时间', value: comic.updateTime },
              { label: '人气', value: comic.views },
              {
                label: '收藏',
                value: `${favorited ? Number.parseInt(comic.favorites) + 1 : comic.favorites}`,
              },
              { label: '最新章节', value: comic.latestChapter },
              { label: '总章节', value: `${comic.chapters.length} 话` },
            ].map((item) => (
              <div key={item.label}>
                <dt className="text-xs text-foreground-500">{item.label}</dt>
                <dd className="mt-0.5 truncate font-medium text-foreground-800">
                  {item.value}
                </dd>
              </div>
            ))}
          </dl>

          <p className="mt-5 text-sm leading-7 text-foreground-600">
            {comic.description}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/read/${comic.id}/1`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-play-circle-fill text-lg"></i>
              开始阅读
            </Link>
            <Link
              to={`/read/${comic.id}/${comic.chapters.length}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-200 bg-background-50 px-6 py-2.5 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-500 hover:text-background-50"
            >
              看最新章节
            </Link>
            <button
              type="button"
              onClick={() => {
                setFavorited((v) => !v);
                showToast(favorited ? '已取消收藏' : '收藏成功，最新章节不错过');
              }}
              className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-6 py-2.5 text-sm font-medium transition-colors ${
                favorited
                  ? 'border-accent-300 bg-accent-500 text-background-50 hover:bg-accent-600'
                  : 'border-accent-200 bg-accent-50 text-accent-700 hover:bg-accent-500 hover:text-background-50'
              }`}
            >
              <i className={favorited ? 'ri-heart-fill' : 'ri-heart-3-line'}></i>
              {favorited ? '已收藏' : '加入收藏'}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-secondary-200 bg-secondary-50 px-6 py-2.5 text-sm font-medium text-secondary-900 transition-colors hover:bg-secondary-500 hover:text-background-50"
            >
              <i className="ri-share-forward-line"></i>
              分享作品
            </button>
          </div>
        </div>
      </section>

      <section className="mt-6 rounded-2xl bg-background-100 p-5 md:p-6" aria-labelledby="chapter-list-title">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="chapter-list-title" className="flex items-center gap-2 text-base font-semibold text-foreground-900">
            <i className="ri-file-list-3-line text-primary-500"></i>
            章节列表
            <span className="text-sm font-normal text-foreground-500">
              共 {comic.chapters.length} 话
            </span>
          </h2>
          <button
            type="button"
            onClick={() => setReverse((v) => !v)}
            className="flex items-center gap-1 whitespace-nowrap rounded-full bg-background-50 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50"
          >
            <i className="ri-swap-line"></i>
            {reverse ? '正序排列' : '倒序排列'}
          </button>
        </div>

        <div className="scroll-thin mt-4 max-h-[420px] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {chapters.map((chapter) => (
              <Link
                key={chapter.id}
                to={`/read/${comic.id}/${chapter.num}`}
                className="flex items-center justify-between gap-2 whitespace-nowrap rounded-lg bg-background-50 px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50"
              >
                <span className="truncate">
                  第{chapter.num}话 {chapter.title}
                </span>
                <i className="ri-bookmark-line shrink-0 text-xs opacity-60"></i>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-10" aria-labelledby="related-title">
        <div className="mb-5 flex items-center justify-between">
          <h2 id="related-title" className="text-xl text-foreground-950 md:text-2xl">
            猜你喜欢
          </h2>
          <Link
            to={`/category/${comic.categories[0]}`}
            className="flex items-center gap-0.5 whitespace-nowrap rounded-full bg-background-100 px-4 py-1.5 text-sm text-primary-600 transition-colors hover:bg-primary-500 hover:text-background-50"
          >
            更多{categoryName}漫画
            <i className="ri-arrow-right-s-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 md:gap-5">
          {related.map((c) => (
            <ComicCard key={c.id} comic={c} />
          ))}
        </div>
      </section>

      <section className="mt-10 rounded-2xl bg-background-100 p-5 md:p-6">
        <h2 className="text-xl text-foreground-950 md:text-2xl">常见问题</h2>
        <div className="mt-4 flex flex-col gap-3">
          {[
            {
              q: `《${comic.title}》漫画在线阅读免费吗？`,
              a: `《${comic.title}》漫画在布丁漫画网完全免费，无需注册、无需付费即可在线畅读全集中文版本。`,
            },
            {
              q: `《${comic.title}》漫画现在更新到第几话？`,
              a: `《${comic.title}》漫画目前已更新至第 ${comic.chapters.length} 话（${comic.latestChapter}），我们会第一时间同步最新连载。`,
            },
            {
              q: `《${comic.title}》漫画多久更新一次？`,
              a: `《${comic.title}》${comic.status === '连载中' ? '保持稳定更新，新章节上线后本站立即同步' : '已完结，全部章节均可一次性畅读'}，请关注作品页的更新时间。`,
            },
            {
              q: '手机和电脑都能在线看漫画吗？',
              a: '可以，布丁漫画网适配手机、平板与电脑浏览器，无需安装任何应用，打开网页即可流畅阅读全部漫画。',
            },
          ].map((item) => (
            <details key={item.q} className="group rounded-xl bg-background-50 p-4 transition-colors open:bg-background-50 md:p-5">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-medium text-foreground-900 md:text-base">
                {item.q}
                <i className="ri-add-line shrink-0 text-lg text-primary-500 transition-transform group-open:rotate-45"></i>
              </summary>
              <p className="mt-3 text-sm leading-6 text-foreground-600">{item.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}